import hash from 'object-hash';

import { isTabletApp } from 'utility/Check/Device';
import {
    getC24App,
    getCheck24Namespace,
    getCheck24Wireframe,
} from 'utility/Provide/Namespace/getCheck24Namespace';
import {
    isCurrentRoutePartOfString,
    isPartOfRouteSet,
    isTravelBookingAll,
} from 'utility/Provide/Route/isCurrentRoutePartOfString';
import { RouteType } from 'utility/Provide/Route/types';
import { getURLParamValue } from 'utility/Provide/Url';
import { cacheRequest, cacheResponse } from 'utility/Request/cache';
import { callApi } from 'utility/Request/callApi';

import { ChattyClientDetailOfferParams } from 'types/chatty/offerParams';
import { DeviceOutput } from 'types/deviceOutput';
import {
    BackendInformationFuncModule,
    ChattyData,
    Environment,
} from 'types/legacy/backendInformation';
import { GlobalNamespace } from 'types/legacy/namespace';
import {
    ChatBotWireframeConfig,
    Check24Wireframe,
} from 'types/legacy/wireframe';
import { ApiEndpoint } from 'types/Request/endpoint';

import { ChatBotResponse } from 'proto/Api/ChatBot';

import { chatConfig } from '../__config/settings';
import { ChatFlowData } from '../types';

export const getChatBotConfig = (
    transactionId: string,
    serviceCode: string,
    offerData?: ChattyClientDetailOfferParams
) => {
    const backendInformationFunc: BackendInformationFuncModule | undefined = (
        getCheck24Namespace() as GlobalNamespace
    )?.components.backendInformationFunc;
    const chattyData = backendInformationFunc.getData<ChattyData>('chatty');
    const Check24 = getCheck24Wireframe();
    const holdingChattyVersion = chattyData?.holdingVersion || '2024-2-21';
    const packageChattyVersion = chattyData?.packageVersion || '2024-2-28';
    const deviceInformation = backendInformationFunc.getDeviceInformation();
    const environment = deviceInformation.environment;
    const isTesting =
        environment === Environment.Testing ||
        environment === Environment.Development;
    const isIntegration = environment === Environment.Integration;
    const chatFlowData = getChatFlowData(isTesting, isIntegration);
    const chatBotConfig: ChatBotWireframeConfig = getEnvironment(
        chatFlowData,
        isTesting
    );
    const holdingRoutes = new Set([
        RouteType.GOLDCLUBLANDINGPAGE,
        RouteType.VOUCHER_APP,
        RouteType.VOUCHER,
        RouteType.POINTS_PROMOTION_TERMS,
        RouteType.POINTS_PROMOTION,
    ]);
    const isHoldingChatbot = isPartOfRouteSet(holdingRoutes);
    const chattyName = isHoldingChatbot ? 'holdingChatty' : 'packageChatty';
    const chattyVersion = isHoldingChatbot
        ? holdingChattyVersion
        : packageChattyVersion;
    const defaultClientDetails = isHoldingChatbot
        ? {}
        : {
              connectionInfoDetails: {
                  serviceCode: serviceCode,
                  pageScope: getPageScope(),
                  transactionId: transactionId,
                  plugin: chatConfig.bridgeConfig.plugin,
              },
          };
    const clientDetails =
        !!offerData && !!defaultClientDetails.connectionInfoDetails
            ? {
                  ...defaultClientDetails,
                  connectionInfoDetails: {
                      ...defaultClientDetails.connectionInfoDetails,
                      offer: offerData,
                  },
              }
            : defaultClientDetails;
    const mainConfig: ChatBotWireframeConfig = {
        ...chatBotConfig,
        chattyVersion: chattyVersion,
        type: 'chatty',
        name: chattyName,
        chatbot: {
            ...chatBotConfig.chatbot,
            client: {
                ...chatBotConfig.chatbot?.client,
                clientDetails: clientDetails,
            },
        },
        element: {
            openChatEvent: 'c24-chat-chatty-open', // event triggered when user clicks on the element
        },
    };

    if (isTabletApp() && !!Check24) {
        tabletAppHandling(Check24, mainConfig);
    }

    return mainConfig;
};

export const getTransactionId = async (serviceCode: string) => {
    const chatBotResponse: ChatBotResponse = await fetchChatBotDataCached(
        serviceCode
    );

    return chatBotResponse?.chatBot?.id || '';
};
export const getPageScope = () => {
    const isCsiOpen = document.body.classList.contains('csi-open');
    if (isCurrentRoutePartOfString(RouteType.TRAVELSEARCH_REGION)) {
        return 2;
    } else if (isCurrentRoutePartOfString(RouteType.TRAVELSEARCH_HOTEL)) {
        return 3;
    } else if (
        isCurrentRoutePartOfString(RouteType.TRAVELSEARCH_OFFER) &&
        !isCsiOpen
    ) {
        return 4;
    } else if (
        isTravelBookingAll() ||
        (isCurrentRoutePartOfString(RouteType.TRAVELSEARCH_OFFER) && isCsiOpen)
    ) {
        return 5;
    }

    return 1;
};
export const getServiceCode = () => {
    const serviceCodeElement = document.querySelector('.js-service-code-code');

    return serviceCodeElement?.textContent || '';
};
const tabletAppHandling = (
    Check24: Check24Wireframe,
    mainConfig: ChatBotWireframeConfig
) => {
    const C24App = getC24App();

    Check24.loader.useChatbotApp = true;
    if (C24App.isAndroidBridgeAvailable() || C24App.isiOSBridgeAvailable()) {
        C24App.setSupportDialogHandler(function () {
            Check24.loader.openChatbot(mainConfig);

            return true; //important, otherwise core would trigger a holding dialog
        });
    }
};
const getEnvironment = (
    chatFlowData: ChatFlowData,
    isTesting: boolean
    //deviceInformation: DeviceInformation
): ChatBotWireframeConfig => {
    // tabletapp ist derzeit noch nicht updatefaähig
    const deviceOutput = DeviceOutput.Desktop;
    const Check24 = getCheck24Wireframe();
    const defaultChatBotConfig: ChatBotWireframeConfig = Check24
        ? JSON.parse(JSON.stringify(Check24.loader.defaultConfigs.chatty))
        : undefined;
    const chatFlowName = chatFlowData.chatFlowName;
    const chatFlowEntry = chatFlowData.chatFlowEntry;
    const chatFlowRelease = chatFlowData.chatFlowRelease;

    if (defaultChatBotConfig.chatbot?.client && Check24) {
        if (isTesting) {
            return {
                ...defaultChatBotConfig,
                env: chatConfig.testingEnvironment.environment,
                deviceoutput: deviceOutput,
                chatbot: {
                    ...defaultChatBotConfig.chatbot,
                    env: chatConfig.testingEnvironment.environment,
                    deviceoutput: deviceOutput,
                    client: {
                        ...defaultChatBotConfig.chatbot.client,
                        controlServerAddress:
                            chatConfig.testingEnvironment.serverAddress,
                        chatFlowName: chatFlowName,
                        chatFlowEntry: chatFlowEntry,
                        chatFlowRelease: chatFlowRelease,
                        presentationType: 'sheet',
                    },
                },
            };
        }

        return {
            ...defaultChatBotConfig,
            chatbot: {
                ...defaultChatBotConfig.chatbot,
                client: {
                    ...defaultChatBotConfig.chatbot.client,
                    chatFlowName: chatFlowName,
                    chatFlowEntry: chatFlowEntry,
                    chatFlowRelease: chatFlowRelease,
                },
            },
        };
    }

    return defaultChatBotConfig;
};
const getChatFlowData = (isTesting: boolean, isIntegration: boolean) => {
    const prepaidVoucherRoutes = new Set([
        RouteType.VOUCHER_APP,
        RouteType.VOUCHER,
        RouteType.POINTS_PROMOTION_TERMS,
        RouteType.POINTS_PROMOTION,
    ]);
    if (isCurrentRoutePartOfString(RouteType.GOLDCLUBLANDINGPAGE)) {
        return chatConfig.holdingConfig.goldClub;
    }
    if (isPartOfRouteSet(prepaidVoucherRoutes)) {
        return chatConfig.holdingConfig.default;
    }
    const chatFlowName = getURLParamValue('chatFlowName');
    if (isTesting) {
        if (chatFlowName) {
            chatConfig.packageConfig.testing.chatFlowName = `pauschalreise.${chatFlowName}`;
        }
        return chatConfig.packageConfig.testing;
    }

    if (isIntegration && chatFlowName) {
        chatConfig.packageConfig.production.chatFlowName = `pauschalreise.${chatFlowName}`;
    }

    return chatConfig.packageConfig.production;
};
const fetchChatBotData = async (serviceCode: string) => {
    const response = await callApi(ApiEndpoint.ChatBot, {
        requestData: {
            serviceCode,
        },
    });
    return response.parsedBody ?? ChatBotResponse.fromPartial({});
};
const fetchChatBotDataCached = cacheRequest(
    cacheResponse(fetchChatBotData, hash),
    hash
);
